/**
 * アナリティクストラッキング用のユーティリティ関数
 */

import { publicApiEndpoint } from './env'
import type { PlayedSecondsReading } from './playedSeconds'

// イベントタイプの定義
export enum AnalyticsEventType {
  SESSION_START = 'SESSION_START',
  SESSION_END = 'SESSION_END',
  VIDEO_START = 'VIDEO_START',
  VIDEO_END = 'VIDEO_END',
  VIDEO_EXIT = 'VIDEO_EXIT',
  VIDEO_PROGRESS = 'VIDEO_PROGRESS',
  BUTTON_CLICK = 'BUTTON_CLICK',
  EXTERNAL_LINK = 'EXTERNAL_LINK',
  MODAL_CLOSE = 'MODAL_CLOSE',
  SURVEY_GATE_ARRIVAL = 'SURVEY_GATE_ARRIVAL',
  SCENE_JUMP = 'SCENE_JUMP',
}

// イベントデータの型定義
export interface AnalyticsEvent {
  scenarioId: string
  sessionId: string
  nodeKey?: string
  eventType: AnalyticsEventType
  eventData?: Record<string, unknown>
  ipAddress?: string
  userAgent?: string
  deviceType?: string
  ga4ClientId?: string
  videoSessionId?: string
  embedPageUrl?: string
}

/**
 * GA4のclient_idを取得（ハイブリッド方式）
 * 1. window.gtag('get', ...) を試行（500msタイムアウト）
 * 2. 失敗時は_ga cookieからパース
 * 3. 両方失敗時はnullを返す
 */
async function getGa4ClientId(): Promise<string | null> {
  if (typeof window === 'undefined') return null

  // 方法1: window.gtag APIから取得
  try {
    const gtagFn = (window as unknown as Record<string, unknown>).gtag as ((...args: unknown[]) => void) | undefined
    if (typeof gtagFn === 'function') {
      const clientId = await new Promise<string | null>((resolve) => {
        const timeout = setTimeout(() => resolve(null), 500)
        try {
          // measurementIdが不明なため、dataLayerからG-XXXを探す
          const dataLayer = (window as unknown as Record<string, unknown>).dataLayer as unknown[] | undefined
          let measurementId: string | undefined
          if (Array.isArray(dataLayer)) {
            for (const entry of dataLayer) {
              if (
                Array.isArray(entry) &&
                entry[0] === 'config' &&
                typeof entry[1] === 'string' &&
                entry[1].startsWith('G-')
              ) {
                measurementId = entry[1]
                break
              }
            }
          }
          if (measurementId) {
            gtagFn('get', measurementId, 'client_id', (id: unknown) => {
              clearTimeout(timeout)
              resolve(typeof id === 'string' ? id : null)
            })
          } else {
            clearTimeout(timeout)
            resolve(null)
          }
        } catch {
          clearTimeout(timeout)
          resolve(null)
        }
      })
      if (clientId) return clientId
    }
  } catch {
    // gtagが利用できない場合は次の方法へ
  }

  // 方法2: _ga cookieからパース（形式: GA1.1.XXXXXXXXXX.YYYYYYYYYY）
  try {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const trimmed = cookie.trim()
      if (trimmed.startsWith('_ga=')) {
        const value = trimmed.substring(4)
        const parts = value.split('.')
        if (parts.length >= 4) {
          return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
        }
      }
    }
  } catch {
    // cookie読み取り失敗時はnullを返す
  }

  return null
}

/**
 * デバイスタイプを検出
 */
function detectDeviceType(): string {
  if (typeof window === 'undefined') return 'unknown'
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return 'mobile'
  }
  if (ua.includes('tablet') || ua.includes('ipad')) {
    return 'tablet'
  }
  return 'desktop'
}

/**
 * セッションIDを生成または取得
 * ローカルストレージに保存し、同一セッション内で再利用
 */
export function getOrCreateSessionId(): string {
  const SESSION_KEY = 'moovy_session_id'
  const SESSION_DURATION = 30 * 60 * 1000 // 30分

  try {
    const stored = localStorage.getItem(SESSION_KEY)
    if (stored) {
      const { sessionId, timestamp } = JSON.parse(stored)
      if (Date.now() - timestamp < SESSION_DURATION) {
        // セッションタイムアウト前にアクセスがあればタイムスタンプを更新
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            sessionId,
            timestamp: Date.now(),
          }),
        )
        return sessionId
      }
    }
  } catch (error) {
    console.warn('Failed to retrieve session ID from storage:', error)
  }

  // 新しいセッションID生成
  const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`

  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        sessionId: newSessionId,
        timestamp: Date.now(),
      }),
    )
  } catch (error) {
    console.warn('Failed to store session ID:', error)
  }

  return newSessionId
}

/**
 * 視聴者セッション情報をlocalStorageに保存
 *
 * GTMのCV計測タグがこの値をもとに「動画視聴者のCV」を判別する。
 * trackEvent の成功レスポンスから tenantId が得られた場合のみ保存する。
 */
function saveMoovyViewerSession(payload: { tenantId: string; sessionId: string; ga4ClientId?: string }): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(
      'moovy_viewer_session',
      JSON.stringify({
        tenantId: payload.tenantId,
        sessionId: payload.sessionId,
        ga4ClientId: payload.ga4ClientId ?? null,
        timestamp: Date.now(),
      }),
    )
  } catch (error) {
    console.warn('Failed to store moovy_viewer_session:', error)
  }
}

/**
 * アナリティクスイベントを送信
 * @param event イベントデータ
 */
export async function trackEvent(
  event: Omit<AnalyticsEvent, 'userAgent' | 'deviceType' | 'ga4ClientId' | 'videoSessionId' | 'embedPageUrl'>,
): Promise<void> {
  try {
    let ga4ClientId: string | undefined
    try {
      const clientId = await getGa4ClientId()
      if (clientId) ga4ClientId = clientId
    } catch {
      // GA4 client_id取得失敗は無視
    }

    const fullEvent: AnalyticsEvent = {
      ...event,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      deviceType: detectDeviceType(),
      ga4ClientId,
      videoSessionId: event.sessionId,
      embedPageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    }

    // 開発環境ではコンソールにも出力
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics Event]', fullEvent)
    }

    // APIエンドポイントに送信
    const response = await fetch(`${publicApiEndpoint}/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fullEvent),
    })

    if (!response.ok) {
      console.warn('Analytics API returned non-OK status:', response.status)
      return
    }

    // CV計測用にtenantIdを取得してlocalStorageへ保存
    try {
      const body = (await response.json()) as { tenantId?: string }
      if (body.tenantId) {
        saveMoovyViewerSession({
          tenantId: body.tenantId,
          sessionId: event.sessionId,
          ga4ClientId,
        })
      }
    } catch {
      // レスポンスJSONパース失敗は無視（CV計測はベストエフォート）
    }
  } catch (error) {
    console.error('Failed to track event:', error)
    // イベント送信の失敗はユーザー体験に影響しないため、エラーを握りつぶす
  }
}

/**
 * セッション開始イベントを送信
 */
export function trackSessionStart(scenarioId: string): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    eventType: AnalyticsEventType.SESSION_START,
  })
}

/**
 * セッション終了イベントを送信
 */
export function trackSessionEnd(scenarioId: string): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    eventType: AnalyticsEventType.SESSION_END,
  })
}

/**
 * 動画再生開始イベントを送信
 */
export function trackVideoStart(
  scenarioId: string,
  nodeKey: string,
  duration?: number,
  /**
   * 一覧型（タイル・スライダー）で前後の動画へ途中で移ったときの、直前の動画の再生秒数。
   * 「最後まで再生」ではないので VIDEO_END は送らず、次の動画の開始にまとめて添える。
   */
  previous?: { nodeKey: string; played: PlayedSecondsReading },
): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.VIDEO_START,
    eventData: {
      duration,
      ...(previous && {
        prevNodeKey: previous.nodeKey,
        prevPlayedSeconds: previous.played.playedSeconds,
        prevVideoDuration: previous.played.videoDuration,
      }),
    },
  })
}

/**
 * 動画再生完了イベントを送信
 */
export function trackVideoEnd(
  scenarioId: string,
  nodeKey: string,
  viewTime: number,
  played?: PlayedSecondsReading,
): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.VIDEO_END,
    eventData: {
      viewTime,
      ...played,
    },
  })
}

/**
 * 動画途中離脱イベントを送信
 */
export function trackVideoExit(scenarioId: string, nodeKey: string, viewTime: number, exitTime: number): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.VIDEO_EXIT,
    eventData: {
      viewTime,
      exitTime,
    },
  })
}

/**
 * 動画視聴進捗イベントを送信
 */
export function trackVideoProgress(scenarioId: string, nodeKey: string, viewTime: number): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.VIDEO_PROGRESS,
    eventData: {
      viewTime,
    },
  })
}

/**
 * ボタンクリックイベントを送信
 */
export function trackButtonClick(
  scenarioId: string,
  nodeKey: string,
  buttonId: string,
  buttonLabel: string,
  targetNodeKey?: string,
  played?: PlayedSecondsReading,
): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.BUTTON_CLICK,
    eventData: {
      buttonId,
      buttonLabel,
      targetNodeKey,
      ...played,
    },
  })
}

/**
 * 外部リンククリックイベントを送信（マクロCV）
 */
export async function trackExternalLink(
  scenarioId: string,
  nodeKey: string,
  url: string,
  buttonLabel?: string,
  played?: PlayedSecondsReading,
): Promise<void> {
  const sessionId = getOrCreateSessionId()

  await trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.EXTERNAL_LINK,
    eventData: {
      url,
      buttonLabel,
      ...played,
    },
  })
}

/**
 * 動画内のクリック領域で、同じ動画の別のシーンへ移動したイベントを送信（ADR-0022）
 * 遷移・外部リンクのクリック領域は既存の BUTTON_CLICK / EXTERNAL_LINK で送る。
 */
export function trackSceneJump(scenarioId: string, nodeKey: string, time: number, buttonLabel: string): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.SCENE_JUMP,
    eventData: {
      time,
      buttonLabel,
    },
  })
}

/**
 * モーダルを閉じたイベントを送信（離脱）
 */
export function trackModalClose(
  scenarioId: string,
  nodeKey?: string,
  viewTime?: number,
  played?: PlayedSecondsReading,
): void {
  const sessionId = getOrCreateSessionId()

  trackEvent({
    scenarioId,
    sessionId,
    nodeKey,
    eventType: AnalyticsEventType.MODAL_CLOSE,
    eventData: {
      viewTime,
      ...played,
    },
  })
}

/**
 * ブラウザのタブごと閉じられたときの離脱を送る（pagehide から呼ぶ）。
 *
 * trackEvent は GA4 の client_id を待ってから送るため、ページが消えると届かない。
 * ここでは待たずに keepalive 付きで送る（ページが閉じても送信を続けられる）。
 * 届かないこともあるが、そのときは従来どおり記録が無いだけで、他の集計は壊れない。
 */
export function trackPageLeave(
  scenarioId: string,
  nodeKey: string | undefined,
  viewTime: number | undefined,
  played: PlayedSecondsReading,
): void {
  if (typeof window === 'undefined') return
  const event: AnalyticsEvent = {
    scenarioId,
    sessionId: getOrCreateSessionId(),
    nodeKey,
    eventType: AnalyticsEventType.MODAL_CLOSE,
    eventData: { viewTime, ...played, reason: 'pagehide' },
    userAgent: window.navigator.userAgent,
    deviceType: detectDeviceType(),
    embedPageUrl: window.location.href,
  }
  event.videoSessionId = event.sessionId
  try {
    void fetch(`${publicApiEndpoint}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(() => {
      // 離脱時の送信失敗は握りつぶす
    })
  } catch {
    // 離脱時の送信失敗は握りつぶす
  }
}
